/**
 * Pre-rasterizes the lobby's IconAvatar SVG into a Pixi Texture, then
 * registers the Texture under the icon's hash. drawCharacter's texture
 * fast-path then draws it pixel-identically to the lobby card.
 *
 * Pipeline:
 *   1. `renderToStaticMarkup(<IconAvatar icon={..} size={256} />)` — SSR
 *      produces deterministic HTML/SVG markup. Lazy-imported so
 *      `react-dom/server` doesn't bloat the entry chunk.
 *   2. DOMParser → extract the inner <svg> + its CSS radial-gradient
 *      backdrop. Wrap into a self-contained SVG document (viewBox 0..100,
 *      a `<radialGradient>` backdrop, a `<clipPath>` for the avatar
 *      circle, then the original inner SVG content).
 *   3. Encode as a `data:image/svg+xml` URL — same-origin under all
 *      browsers including mobile Safari (blob URLs occasionally trip up
 *      Safari's `<img>`-SVG path).
 *   4. Load via HTMLImageElement → drawImage onto a 2D canvas at 4× the
 *      logical 100×100 viewBox (400×400 raster) for crisp display under
 *      arbitrary in-game scaling.
 *   5. `Texture.from(canvas)` — wraps the canvas as a Pixi texture
 *      source. drawCharacter's texture fast-path renders this as a
 *      Sprite, sized to whatever each game asks for.
 *
 * Why not Pixi's native `Graphics.svg()`? It silently skips radial
 * gradients (`parseSVGDefinitions.js` has a hardcoded `warn(…not yet
 * supported)`), ignores transforms, and drops clip-paths — i.e. it
 * can't faithfully render the lobby's SVG. The browser's SVG renderer
 * supports all of those; using it as the rasterizer is the only path
 * that produces output matching the lobby card.
 */
import { Texture, type Application } from 'pixi.js';
import { createElement } from 'react';
import type { CharacterIcon } from '@tt/types';
import {
  iconHash,
  setCharacterTextureByIconHash,
  clearCharacterTextures,
} from '@tt/character-render';
import { IconAvatar } from '../characters/IconAvatar';
import type { IconValue } from '../characters/IconAvatar';

const SVG_NS = 'http://www.w3.org/2000/svg';
/** 4× the 100×100 viewBox = sharp at typical in-game sprite sizes (22–70 px). */
const RASTER_SIZE = 400;

interface ParsedAvatar {
  bgFill: string;
  bgFillDark: string;
  innerSvgXml: string;
}

let cachedRenderer: ((el: ReturnType<typeof createElement>) => string) | null = null;
async function getRenderer(): Promise<(el: ReturnType<typeof createElement>) => string> {
  if (cachedRenderer) return cachedRenderer;
  const mod = await import('react-dom/server');
  cachedRenderer = mod.renderToStaticMarkup;
  return cachedRenderer;
}

async function renderAndExtract(icon: IconValue, size: number): Promise<ParsedAvatar | null> {
  const renderToStaticMarkup = await getRenderer();
  const html = renderToStaticMarkup(createElement(IconAvatar, { icon, size }));
  if (!html) return null;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const span = doc.querySelector('span.icon-avatar') as HTMLElement | null;
  if (!span) {
    console.warn('[iconTexture] no .icon-avatar span in rendered markup', html.slice(0, 200));
    return null;
  }
  const svgEl = span.querySelector('svg');
  if (!svgEl) {
    console.warn('[iconTexture] no <svg> in IconAvatar output');
    return null;
  }

  const bgCss = span.getAttribute('style') || '';
  const m = bgCss.match(/(hsl\([^)]+\))\s*0%\s*,\s*(hsl\([^)]+\))\s*100%/);
  const bgFill = m?.[1] ?? 'hsl(20 40% 20%)';
  const bgFillDark = m?.[2] ?? 'hsl(20 40% 8%)';

  const serializer = new XMLSerializer();
  const parts: string[] = [];
  for (let i = 0; i < svgEl.childNodes.length; i++) {
    const node = svgEl.childNodes[i];
    if (!node) continue;
    parts.push(serializer.serializeToString(node));
  }
  return { bgFill, bgFillDark, innerSvgXml: parts.join('') };
}

function buildSvgDocument(parsed: ParsedAvatar): string {
  // Faithful reproduction of the lobby's avatar:
  //   span CSS radial-gradient backdrop → SVG <radialGradient>
  //   span border-radius:50% → SVG <clipPath> on a circle
  //   span's child svg's children → drawn inside the clip
  // The browser renders all of this perfectly; we never modify the
  // inner SVG markup, so all richSvg cosmetics show up identically.
  return `<svg xmlns="${SVG_NS}" viewBox="0 0 100 100" width="${RASTER_SIZE}" height="${RASTER_SIZE}">`
    + `<defs>`
    +   `<radialGradient id="ttBg" cx="35%" cy="30%" r="65%">`
    +     `<stop offset="0%" stop-color="${parsed.bgFill}"/>`
    +     `<stop offset="100%" stop-color="${parsed.bgFillDark}"/>`
    +   `</radialGradient>`
    +   `<clipPath id="ttClip"><circle cx="50" cy="50" r="50"/></clipPath>`
    + `</defs>`
    + `<g clip-path="url(#ttClip)">`
    +   `<circle cx="50" cy="50" r="50" fill="url(#ttBg)"/>`
    +   parsed.innerSvgXml
    + `</g>`
    + `</svg>`;
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image load failed'));
    img.src = url;
  });
}

async function rasterize(svg: string): Promise<HTMLCanvasElement | null> {
  // Data URL keeps the SVG same-origin under every browser (including
  // mobile Safari, which has historically had `<img>`+blob-URL SVG
  // quirks). `encodeURIComponent` handles the # / ( ) and other chars
  // that need escaping in a URL.
  const dataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  const img = await loadImage(dataUrl);

  const canvas = document.createElement('canvas');
  canvas.width = RASTER_SIZE;
  canvas.height = RASTER_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.clearRect(0, 0, RASTER_SIZE, RASTER_SIZE);
  ctx.drawImage(img, 0, 0, RASTER_SIZE, RASTER_SIZE);
  return canvas;
}

async function loadIconTexture(icon: CharacterIcon): Promise<Texture | null> {
  const parsed = await renderAndExtract(icon as IconValue, 100);
  if (!parsed) return null;
  const svg = buildSvgDocument(parsed);
  const canvas = await rasterize(svg);
  if (!canvas) return null;
  const tex = Texture.from(canvas);
  if (tex) {
    setCharacterTextureByIconHash(iconHash(icon), tex);
  }
  return tex;
}

export async function preloadMatchCharacterTextures(
  players: Array<{ icon?: CharacterIcon | undefined }>,
  _app: Application,
): Promise<void> {
  const tasks = players
    .filter((p): p is { icon: CharacterIcon } => !!p.icon)
    .map((p) => loadIconTexture(p.icon).catch((e) => {
      console.warn('[iconTexture] preload failed', e);
      return null;
    }));
  const results = await Promise.all(tasks);
  const ok = results.filter((r) => r !== null).length;
  console.log(`[iconTexture] preloaded ${ok}/${tasks.length} character textures`);
}

export function clearMatchCharacterTextures(): void {
  clearCharacterTextures();
}
